<template>
  <div ref="sliderRef" class="readiness-slider">
    <div class="slider-track">
      <div class="track-segment segment-1"></div>
      <div class="track-segment segment-2"></div>
      <div class="track-segment segment-3"></div>
      <div class="track-segment segment-4"></div>
      <div class="track-segment segment-5"></div>
      <div class="track-segment segment-6"></div>
    </div>
    <EXTooltip
      :manual="true"
      v-model="isDragging"
      placement="top"
      :title="tooltipText"
      :delay="0"
    >
      <div
        ref="thumbRef"
        class="slider-thumb"
        :style="{ left: thumbPosition }"
        @mousedown="handleMouseDown"
        @touchstart="handleTouchStart"
      ></div>
    </EXTooltip>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import EXTooltip from '@components/tooltip/EXTooltip.vue';

const props = defineProps({
  readiness: {
    type: Number,
    required: true,
    validator: (value) => {
      return value >= 0 && value <= 150 && value % 5 === 0;
    }
  }
});

const emit = defineEmits(['update:modelValue']);

const TRACK_WIDTH = 515;
const MIN_VALUE = 0;
const MAX_VALUE = 150;
const STEP = 5;

const isDragging = ref(false);
const currentReadiness = ref(props.readiness);
const dragStartX = ref(0);
const dragStartValue = ref(0);
const thumbRef = ref(null);
const sliderRef = ref(null);

watch(
  () => props.readiness,
  (value) => {
    currentReadiness.value = value;
  }
);

function getTrackWidthPx() {
  const width = sliderRef.value?.getBoundingClientRect()?.width ?? 0;
  return width > 0 ? width : TRACK_WIDTH;
}

const thumbPosition = computed(() => {
  const percentage = (currentReadiness.value / MAX_VALUE) * 100;
  return `${percentage}%`;
});

const tooltipText = computed(() => {
  return `${currentReadiness.value}%`;
});

const snapToStep = (value) => {
  return Math.round(value / STEP) * STEP;
};

const clampValue = (value) => {
  return Math.max(MIN_VALUE, Math.min(MAX_VALUE, value));
};

const handleMouseDown = (event) => {
  event.preventDefault();
  isDragging.value = true;
  dragStartX.value = event.clientX;
  dragStartValue.value = currentReadiness.value;

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

const handleTouchStart = (event) => {
  event.preventDefault();
  isDragging.value = true;
  const touch = event.touches[0];
  dragStartX.value = touch.clientX;
  dragStartValue.value = currentReadiness.value;

  document.addEventListener('touchmove', handleTouchMove);
  document.addEventListener('touchend', handleTouchEnd);
};

const handleMouseMove = (event) => {
  if (!isDragging.value) return;

  const deltaX = event.clientX - dragStartX.value;
  const deltaValue = (deltaX / getTrackWidthPx()) * MAX_VALUE;
  const newValue = dragStartValue.value + deltaValue;
  const clampedValue = clampValue(newValue);
  const snappedValue = snapToStep(clampedValue);

  currentReadiness.value = snappedValue;
  emit('update:modelValue', snappedValue);
};

const handleTouchMove = (event) => {
  if (!isDragging.value) return;

  const touch = event.touches[0];
  const deltaX = touch.clientX - dragStartX.value;
  const deltaValue = (deltaX / getTrackWidthPx()) * MAX_VALUE;
  const newValue = dragStartValue.value + deltaValue;
  const clampedValue = clampValue(newValue);
  const snappedValue = snapToStep(clampedValue);

  currentReadiness.value = snappedValue;
  emit('update:modelValue', snappedValue);
};

const handleMouseUp = () => {
  if (!isDragging.value) return;

  isDragging.value = false;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
};

const handleTouchEnd = () => {
  if (!isDragging.value) return;

  isDragging.value = false;
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
};

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', handleMouseUp);
  document.removeEventListener('touchmove', handleTouchMove);
  document.removeEventListener('touchend', handleTouchEnd);
});

defineExpose({
  isDragging,
  tooltipText,
  currentReadiness,
  thumbPosition
});
</script>

<style scoped>
.readiness-slider {
  position: relative;
  width: 100%;
  max-width: 515px;
  height: 13px;
  user-select: none;
}

.slider-track {
  position: absolute;
  top: 3.5px;
  left: 0;
  right: 0;
  width: 100%;
  height: 6px;
  display: flex;
}

.track-segment {
  flex: 1 1 0;
  min-width: 0;
  height: 6px;
}

.track-segment:first-child {
  border-radius: 3px 0 0 3px;
}

.track-segment:last-child {
  border-radius: 0 3px 3px 0;
}

.segment-1 {
  background-color: #f04438;
}

.segment-2 {
  background-color: #f79009;
}

.segment-3 {
  background-color: #fac515;
}

.segment-4 {
  background-color: #17b26a;
}

.segment-5 {
  background-color: #36bffa;
}

.segment-6 {
  background-color: #6938ef;
}

.slider-thumb {
  position: absolute;
  top: 0;
  width: 13px;
  height: 13px;
  margin-left: -6.5px;
  background-color: #ffffff;
  border: 1px solid #101828;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.slider-thumb:hover {
  box-shadow: 0 4px 6px rgba(16, 24, 40, 0.1), 0 2px 4px rgba(16, 24, 40, 0.06);
}

.slider-thumb:active {
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}
</style>
